import { useEffect, useMemo, useState } from 'react';
import {
  fetchInterviewAvailability,
  type AvailabilityRole,
} from '../../services/interviewAvailabilityApi';
import type { AvailabilityByPosting } from '../../types/interviewAvailability';

/**
 * Per-page memory of the last answer, keyed by door + roles. The Case and Final
 * tabs ask for the same roles, and switching between them should not re-read
 * every panelist's calendar — the proxy caches too, this only saves the trip.
 */
const MEMO_TTL_MS = 5 * 60 * 1000;
const memo = new Map<string, { at: number; data: AvailabilityByPosting }>();

/**
 * The light for a list of roles. Degrades to `{}` on failure: a missing light
 * must never take a tab down with it, so the error is returned for whoever
 * wants to mention it and nothing else depends on it.
 */
export function useInterviewAvailability(
  door: 'radar' | 'board',
  roles: AvailabilityRole[],
  gmailToken?: string | null
): { data: AvailabilityByPosting; loading: boolean; error: string | null } {
  const key = useMemo(
    () => `${door}|${roles.map((r) => r.postingId).sort().join(',')}`,
    [door, roles]
  );
  const [state, setState] = useState<{ key: string; data: AvailabilityByPosting; error: string | null } | null>(
    null
  );
  // With a token the proxy can read kick-off sheets it could not read before,
  // so its arrival is a reason to ask again.
  const hasToken = Boolean(gmailToken);

  useEffect(() => {
    if (roles.length === 0) return;
    const hit = memo.get(key);
    if (hit && Date.now() - hit.at < MEMO_TTL_MS && !hasToken) return;
    let cancelled = false;
    fetchInterviewAvailability(door, roles, gmailToken)
      .then((data) => {
        memo.set(key, { at: Date.now(), data });
        if (!cancelled) setState({ key, data, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState((prev) => ({
            key,
            data: prev?.key === key ? prev.data : {},
            error: err instanceof Error ? err.message : 'Could not read availability',
          }));
        }
      });
    return () => {
      cancelled = true;
    };
    // `roles` is captured through `key`; re-running on a new array with the same ids is waste.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, hasToken]);

  // A remembered answer is shown straight away, however old: the effect above
  // replaces it when it is past MEMO_TTL_MS, and a stale light beats a blank one.
  const remembered = memo.get(key);
  const current = state?.key === key ? state : remembered ? { key, data: remembered.data, error: null } : null;
  return {
    data: current?.data ?? {},
    loading: roles.length > 0 && !current,
    error: current?.error ?? null,
  };
}
